type AttrValue = string | number | boolean | undefined | null;
type Child = Node | string | null | undefined | false;

const ATTR_ALLOW = new Set([
  "class",
  "id",
  "type",
  "value",
  "name",
  "for",
  "role",
  "rel",
  "target",
  "title",
  "placeholder",
  "maxlength",
  "min",
  "max",
  "step",
  "rows",
  "tabindex",
  "hidden",
  "disabled",
  "readonly",
  "open",
  "download",
  "inputmode",
  "autocomplete",
  "datetime",
]);

const blobUrls = new Set<string>();

export function registerBlobUrl(url: string): string {
  blobUrls.add(url);
  return url;
}

export function safeUrl(value: string): string {
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  if (value.startsWith("https:")) return value;
  if (value.startsWith("blob:") && blobUrls.has(value)) return value;
  throw new Error("blocked url");
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, AttrValue> | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value === undefined || value === false || value === null) continue;
      if (key === "style") continue;
      if (key === "class") {
        node.className = String(value);
        continue;
      }
      if (key === "text") {
        node.textContent = String(value);
        continue;
      }
      if (key === "disabled") {
        const on = value === true || value === "true" || value === "";
        if (
          node instanceof HTMLButtonElement ||
          node instanceof HTMLInputElement ||
          node instanceof HTMLSelectElement ||
          node instanceof HTMLTextAreaElement ||
          node instanceof HTMLOptionElement
        ) {
          node.disabled = on;
        }
        continue;
      }
      if ((key === "readOnly" || key === "readonly") && (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement)) {
        node.readOnly = value === true || value === "true" || value === "";
        continue;
      }
      if (key === "href" || key === "src") {
        node.setAttribute(key, safeUrl(String(value)));
        continue;
      }
      const lower = key.toLowerCase();
      if (ATTR_ALLOW.has(lower) || lower.startsWith("data-") || lower.startsWith("aria-")) {
        node.setAttribute(key, value === true ? "" : String(value));
      }
    }
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    if (typeof child === "string") node.append(document.createTextNode(child));
    else node.append(child);
  }
  return node;
}

export function clear(node: Element): void {
  node.replaceChildren();
}

export async function copyText(text: string, input?: HTMLInputElement): Promise<"copied" | "fallback"> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return "copied";
    }
  } catch {
    /* fall through */
  }
  if (input) {
    input.focus();
    input.select();
  }
  return "fallback";
}
